import { BillingEntityTypeEnum } from '@api/READ_ONLY/billing_api/Api';
import { DEFAULT_CURRENCY } from '@app/common/constants';
import { isExternalProvidersEnabled } from '@app/features/externalProviders/featureFlag';

import { getDatePickerLocaleByLanguage, getNumberFormattingLocaleByLanguage } from '@app/i18n/helpers';
import { APP_PREFIX, VISITS_PAGE } from '@app/routes/urls';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiUsers } from 'react-icons/fi';
import { LuStethoscope } from 'react-icons/lu';
import { MdOutlineBusiness, MdOutlinePerson, MdOutlinePointOfSale } from 'react-icons/md';
import { RiNurseFill } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';

import UpdateConductedPaymentModal from '../components/UpdateConductedPaymentModal/UpdateConductedPaymentModal';
import CustomerProfileFilters from './CustomerProfileFilters';
import CustomerProfileLedgerTable, { LedgerRow } from './CustomerProfileLedgerTable';
import { type EntityType, useCustomerProfile } from './useCustomerProfile';

const CustomerProfile = () => {
  const { t, i18n: localization } = useTranslation();
  const navigate = useNavigate();

  // THIS IS HUGE NEEDS TO BREAK DOWN -- FOR NOW ITS OK TILL WE HAVE EVERYTHING FROM BUSINESS AND BE
  const {
    entityType,
    entityIdentifier,
    startingDate,
    endingDate,
    fetchLedger,
    canFetchLedger,
    ledgerKey,
    ledgerRefreshToken,
    reloadLedger,
    getLedgerExportParams,
    editingPaymentId,
    editingEntityIdentifier,
    selectedClient,
    selectedDoctor,
    selectedCaregiver,
    selectedProvider,
    changeEntityType,
    changeEntityIdentifier,
    changeStartingDate,
    changeEndingDate,
    openEditModal,
    closeEditModal,
    deletePaymentById,
  } = useCustomerProfile();

  // The total sums the rows on the current page, as it always has.
  const [pageRows, setPageRows] = useState<LedgerRow[]>([]);
  const currentBalanceTotal = useMemo(() => pageRows.reduce((sum, row) => sum + row.amount, 0), [pageRows]);

  const locale = getNumberFormattingLocaleByLanguage(localization.language);
  const datePickerLocale = getDatePickerLocaleByLanguage(localization.language);
  const dateFormat = t('date-format');

  const typeOptions: Array<{ value: EntityType; label: string }> = [
    { value: BillingEntityTypeEnum.Client, label: t('clients') },
    { value: BillingEntityTypeEnum.Doctor, label: t('doctors') },
    { value: BillingEntityTypeEnum.Caregiver, label: t('caregivers') },
    { value: BillingEntityTypeEnum.CaregiverCashRegister, label: t('caregiver-cash-register') },
    // Feature off: hidden, unless the page was opened on a provider's ledger.
    ...(isExternalProvidersEnabled() || entityType === BillingEntityTypeEnum.Provider
      ? [{ value: BillingEntityTypeEnum.Provider, label: t('providers') }]
      : []),
  ];

  const balanceColorClass =
    currentBalanceTotal > 0 ? 'text-emerald-600' : currentBalanceTotal < 0 ? 'text-rose-500' : 'text-slate-400';

  const iconByEntityType: Partial<Record<BillingEntityTypeEnum, typeof MdOutlinePerson>> = {
    [BillingEntityTypeEnum.Client]: MdOutlinePerson,
    [BillingEntityTypeEnum.Doctor]: LuStethoscope,
    [BillingEntityTypeEnum.Caregiver]: RiNurseFill,
    [BillingEntityTypeEnum.CaregiverCashRegister]: MdOutlinePointOfSale,
    [BillingEntityTypeEnum.Provider]: MdOutlineBusiness,
  };

  const colorByEntityType: Partial<Record<BillingEntityTypeEnum, string>> = {
    [BillingEntityTypeEnum.Client]: '#16a34a',
    [BillingEntityTypeEnum.Doctor]: '#94a3b8',
    [BillingEntityTypeEnum.Caregiver]: '#7dd3fc',
    [BillingEntityTypeEnum.CaregiverCashRegister]: '#f59e0b',
    [BillingEntityTypeEnum.Provider]: '#7c3aed',
  };

  const EntityIcon = iconByEntityType[entityType] ?? iconByEntityType[BillingEntityTypeEnum.Client]!;
  const entityColor = colorByEntityType[entityType] ?? colorByEntityType[BillingEntityTypeEnum.Client]!;
  const entityTint = `${entityColor}1a`;
  const formattedCurrentBalanceTotal =
    currentBalanceTotal === 0
      ? currencyFormatter(0, DEFAULT_CURRENCY, locale)
      : `${currentBalanceTotal > 0 ? '+' : '-'}${currencyFormatter(Math.abs(currentBalanceTotal), DEFAULT_CURRENCY, locale)}`;

  // Stable handlers: they feed the ledger's column memo.
  const handleEdit = useCallback(
    (row: LedgerRow) => openEditModal(row.paymentId, row.entityIdentifier),
    [openEditModal]
  );

  const handleDelete = useCallback((row: LedgerRow) => deletePaymentById(row.paymentId), [deletePaymentById]);

  const handleVisitClick = useCallback(
    (visitId: number) => navigate(`/${APP_PREFIX}/${VISITS_PAGE}/${visitId}/edit`),
    [navigate]
  );

  const ledgerEmptyText = useMemo(
    () =>
      !entityIdentifier ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-slate-100 text-slate-600">
            <FiUsers size={18} />
          </span>
          <p className="text-base font-semibold tracking-wide text-slate-700">{t('select-entity-first')}</p>
        </div>
      ) : undefined,
    [entityIdentifier, t]
  );

  return (
    <div className="h-[calc(100vh-100px)] overflow-hidden bg-slate-50">
      <div className="flex h-full w-full flex-col">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="w-fit rounded-lg border border-slate-200 bg-white px-5 py-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-3">
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-full border"
                  style={{
                    borderColor: entityColor,
                    backgroundColor: entityTint,
                    color: entityColor,
                  }}
                >
                  <EntityIcon size={12} />
                </span>
                <span className="text-lg font-semibold text-slate-900">{t('total')}</span>
              </div>
              <div className="flex items-baseline gap-1 text-xl font-semibold">
                <span className="text-slate-400">:</span>
                <span className={balanceColorClass}>{formattedCurrentBalanceTotal}</span>
              </div>
            </div>
          </div>

          <CustomerProfileFilters
            entityTypeValue={entityType}
            entityTypeOptions={typeOptions}
            entityIdentifierValue={entityIdentifier}
            startingDateValue={startingDate}
            endingDateValue={endingDate}
            dateFormat={dateFormat}
            datePickerLocale={datePickerLocale}
            onEntityTypeChange={changeEntityType}
            onEntityIdentifierChange={changeEntityIdentifier}
            onStartingDateChange={changeStartingDate}
            onEndingDateChange={changeEndingDate}
            selectPlaceholder={t('select')}
            startingDatePlaceholder={t('from')}
            endingDatePlaceholder={t('to')}
            selectedClient={selectedClient}
            selectedDoctor={selectedDoctor}
            selectedCaregiver={selectedCaregiver}
            selectedProvider={selectedProvider}
          />
        </div>

        <div className="mt-6 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
          <CustomerProfileLedgerTable
            key={ledgerKey}
            entityType={entityType}
            currency={DEFAULT_CURRENCY}
            locale={locale}
            emptyText={ledgerEmptyText}
            fetchData={fetchLedger}
            refreshToken={ledgerRefreshToken}
            getExportParams={canFetchLedger ? getLedgerExportParams : undefined}
            onDataChange={setPageRows}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onVisitClick={handleVisitClick}
          />
        </div>
      </div>

      <UpdateConductedPaymentModal
        paymentId={editingPaymentId}
        entityIdentifier={editingEntityIdentifier}
        onClose={closeEditModal}
        onSaved={reloadLedger}
      />
    </div>
  );
};

export default CustomerProfile;
